import React, { useCallback, useEffect, useState } from "react";
import { Button, Form } from "antd";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectCategories } from "../../redux/features/category/categorySlice";
import { addCategory, deleteCategory, fetchCategories, updateCategory } from "../../redux/features/category/categoryAsyncThunk";
import { ActivationStatus, Category, Question, SubCategory } from "../../types";
import { selectSubCategories } from "../../redux/features/subCategory/subCategorySlice";
import { createSubCategory, deleteSubCategory, fetchSubCategories, updateSubCategory } from "../../redux/features/subCategory/subCategoryAsyncThunk";
import CreateEditCategoryModal from "./CategoryModal";
import CreateEditSubCategoryModal from "./SubCategoryModal";
import CreateEditQuestionModal from "./QuestionModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { createQuestion, deleteQuestion, fetchQuestions, updateQuestion } from "../../redux/features/question/questionAsyncThunk";
import { selectQuestion, setQuestionData, setSelectedSubCategory as setSelectedSubCategoryInQuestion } from "../../redux/features/question/questionSlice";
import { ItemNameText, ItemNameWrapper, MobileView, StyledContent, StyledLayout, WebView } from "./styles";
import { ReactComponent as BoxIcon } from "../../assets/images/BoxIcon.svg";
import { ReactComponent as SubBoxIcon } from "../../assets/images/SubBoxIcon.svg";
import { CollapseSection, ItemColumn, ItemLabel, ItemList } from "./CategoryConst";
import { sortOptionsAlphabetically } from "../../utils/helpers/sortOptionsAlphabetically";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { permissions } from "../../utils/constants/permissions";
import { selectUserRole } from "../../redux/features/auth/selectors";

const ItemName: React.FC<{ className?: string; icon?: any; name: string }> = ({ className, icon, name }) => {
  return (
    <>
      <ItemNameWrapper className={className}>
        {icon && icon} <ItemNameText>{name}</ItemNameText>
      </ItemNameWrapper>
    </>
  );
};

const CategoryPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: categories, status: categoryStatus } = useAppSelector(selectCategories);
  const { data: subCategories, status: subCategoryStatus } = useAppSelector(selectSubCategories);
  const { data: questions, status: questionStatus, total: totalQuestion } = useAppSelector(selectQuestion);
  const userRole = useAppSelector(selectUserRole);

  useEffect(() => {
    if (categoryStatus === "idle") {
      dispatch(fetchCategories());
    }
  }, [dispatch, categoryStatus]);

  useEffect(() => {
    if (!subCategories.length && subCategoryStatus === "idle") dispatch(fetchSubCategories());
  }, [dispatch, subCategories]);

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [modalType, setModalType] = useState<"category" | "subcategory" | "question" | null>(null);
  const [editingRecord, setEditingRecord] = useState<Category | SubCategory | Question | null>(null);
  const [showDeleteConfirmItem, setShowDeleteConfirmItem] = useState<{ item: Category | SubCategory | Question; type: "category" | "subcategory" | "question" } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [form] = Form.useForm();

  const [isSubCategoryOpen, setSubCategoryisopen] = useState(false);
  const [isQuestionsOpen, setQuestionsisopen] = useState(false);

  useEffect(() => {
    if (modalType === "question") {
      form.setFieldValue("name", "question");
    }
  }, [modalType, form]);

  const onCancel = () => {
    setModalType(null);
    form.resetFields();
    setEditingRecord(null);
  };

  const sanitizeTranslations = (input: any) => {
    if (!input || typeof input !== "object") return undefined;
    const entries = Object.entries(input).filter(([, v]) => v && `${v}`.trim() !== "");
    return entries.length ? Object.fromEntries(entries) : undefined;
  };

  const handleSave = async (values: any) => {
    try {
      if (modalType === "category") {
        if (form.getFieldValue("id")) {
          await dispatch(updateCategory({ ...editingRecord, ...values, nameTranslations: sanitizeTranslations(values.nameTranslations) })).unwrap();
        } else {
          await dispatch(addCategory({ ...values, status: ActivationStatus.Active, nameTranslations: sanitizeTranslations(values.nameTranslations) })).unwrap();
        }
      } else if (modalType === "subcategory") {
        if (form.getFieldValue("id")) {
          await dispatch(updateSubCategory({ id: form.getFieldValue("id"), data: { ...values, status: (editingRecord as SubCategory)?.status, nameTranslations: sanitizeTranslations(values.nameTranslations) } })).unwrap();
        } else {
          await dispatch(createSubCategory({ ...values, status: ActivationStatus.Active, nameTranslations: sanitizeTranslations(values.nameTranslations) })).unwrap();
        }
        // Refresh subcategories so latest translations appear
        dispatch(fetchSubCategories());
      } else if (modalType === "question") {
        if (form.getFieldValue("id")) {
          await dispatch(updateQuestion({ ...editingRecord, ...values, fieldNameTranslations: sanitizeTranslations(values.fieldNameTranslations), placeholderTranslations: sanitizeTranslations(values.placeholderTranslations) }));
        } else {
          await dispatch(createQuestion({ ...values, fieldNameTranslations: sanitizeTranslations(values.fieldNameTranslations), placeholderTranslations: sanitizeTranslations(values.placeholderTranslations) }));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      onCancel();
    }
  };

  useEffect(() => {
    const subCategory = subCategories.find((c) => c.id === selectedSubCategory?.id);
    if (!subCategory) {
      dispatch(setSelectedSubCategoryInQuestion(null));
      setSelectedSubCategory(null);
      dispatch(setQuestionData([]));
    } else {
      dispatch(setSelectedSubCategoryInQuestion(subCategory));
      setSelectedSubCategory(subCategory);
    }
  }, [subCategories]);

  useEffect(() => {
    const category = categories.find((c) => c.id === selectedCategory?.id);
    if (!category) {
      dispatch(setSelectedSubCategoryInQuestion(null));
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      dispatch(setQuestionData([]));
    } else {
      setSelectedCategory(category);
    }
  }, [categories]);

  useEffect(() => {
    if (!subCategories.find((c) => c.id === selectedSubCategory?.id)) {
      dispatch(setSelectedSubCategoryInQuestion(null));
      setSelectedSubCategory(null);
      dispatch(setQuestionData([]));
    }
  }, [subCategories]);

  useEffect(() => {
    if (!categories.find((c) => c.id === selectedCategory?.id)) {
      dispatch(setSelectedSubCategoryInQuestion(null));
      setSelectedCategory(null);
      setSelectedCategory(null);
      dispatch(setQuestionData([]));
    }
  }, [categories]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (showDeleteConfirmItem?.item?.id) {
        if (showDeleteConfirmItem.type === "category") {
          await dispatch(deleteCategory({ id: showDeleteConfirmItem?.item?.id })).unwrap();
        } else if (showDeleteConfirmItem.type === "subcategory") {
          await dispatch(deleteSubCategory({ id: showDeleteConfirmItem?.item?.id })).unwrap();
        } else if (showDeleteConfirmItem.type === "question") {
          await dispatch(deleteQuestion(showDeleteConfirmItem?.item?.id)).unwrap();
        }
      }
    } catch (error) {
      console.error("delete error: ", error);
    } finally {
      setShowDeleteConfirmItem(null);
      setIsDeleting(false);
    }
  };

  const handleCategoryClick = (category: Category) => {
    dispatch(setQuestionData([]));
    setSelectedCategory(category);
    setSelectedSubCategory(null);
    setSelectedSubCategoryInQuestion(null);
  };

  const handleSubcategoryClick = (subCategory: SubCategory) => {
    setSelectedSubCategory(subCategory);
    dispatch(setSelectedSubCategoryInQuestion(subCategory));
    dispatch(setQuestionData([]));
    dispatch(
      fetchQuestions({
        search_query: "",
        offset: 0,
        limit: 20,
        subCategoryId: subCategory?.id,
      })
    );
  };

  const handleLoadMoreQuestions = () => {
    dispatch(
      fetchQuestions({
        search_query: "",
        offset: questions.length,
        limit: 20,
        subCategoryId: selectedSubCategory?.id,
      })
    );
  };

  const handleCategoryItemClick = () => {
    setSubCategoryisopen(true);
  };

  const handleSubCategoryItemClick = (e: any) => {
    setQuestionsisopen(true);
  };

  const handleQuestionsItemClick = (e: any) => {
    setQuestionsisopen(true);
  };

  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );

  return (
    <StyledLayout>
      <WebView>
        <StyledContent>
          {/* Category Column */}
          <ItemColumn
            title="Category"
            isStatus={true}
            hasAddPermission={hasPermission(permissions.AddEditCategory)}
            hasEditPermission={hasPermission(permissions.AddEditCategory)}
            hasDeletePermission={hasPermission(permissions.DeleteCategory)}
            hasViewPermission={hasPermission(permissions.ViewCategory)}
            buttonLabel="+ Add"
            onAddClick={() => {
              setModalType("category");
            }}
            loading={categoryStatus === "loading"}
            dataSource={sortOptionsAlphabetically(categories, "name")}
            isSelected={selectedCategory?.id}
            isSelectedItemOnClick={(item: any) => selectedCategory?.id !== item.id && handleCategoryClick(item)}
            onEdit={(item: any) => {
              setEditingRecord(item);
              form.setFieldsValue(item);
              setModalType("category");
            }}
            onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "category" })}
            isShowDelete={false}
          />

          {/* Subcategory Column */}
          <ItemColumn
            title="Sub Category"
            buttonLabel="+ Add"
            onAddClick={() => {
              form.setFieldsValue({ category: selectedCategory?.id });
              setModalType("subcategory");
            }}
            hasAddPermission={hasPermission(permissions.AddEditSubCategory)}
            hasEditPermission={hasPermission(permissions.AddEditSubCategory)}
            hasDeletePermission={hasPermission(permissions.DeleteSubCategory)}
            hasViewPermission={hasPermission(permissions.ViewSubCategory)}
            isStatus={true}
            header={selectedCategory ? <ItemName className="bold" icon={<BoxIcon />} name={`Selected Category: ${selectedCategory?.name}`} /> : undefined}
            loading={subCategoryStatus === "loading"}
            dataSource={selectedCategory?.id ? sortOptionsAlphabetically(subCategories?.filter((subCategory) => subCategory?.category?.id === selectedCategory?.id), "name") : []}
            isSelected={selectedSubCategory?.id}
            isSelectedItemOnClick={(item: any) => selectedSubCategory?.id !== item.id && handleSubcategoryClick(item)}
            onEdit={(item: any) => {
              setEditingRecord(item);
              form.setFieldsValue({
                ...item,
                category: item?.category?.id,
                translationKey: item?.translationKey || undefined,
                nameTranslations: typeof item?.nameTranslations === "string" ? (() => { try { return JSON.parse(item.nameTranslations); } catch { return {}; } })() : (item?.nameTranslations || {}),
              });
              setModalType("subcategory");
            }}
            onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "subcategory" })}
          />

          {/* Questions Column */}
          <ItemColumn
            title="Questions"
            buttonLabel="+ Add"
            hasAddPermission={hasPermission(permissions.AddEditQuestion)}
            hasEditPermission={hasPermission(permissions.AddEditQuestion)}
            hasDeletePermission={hasPermission(permissions.DeleteQuestion)}
            hasViewPermission={hasPermission(permissions.ViewQuestion)}
            onAddClick={() => {
              setModalType("question");
              form.setFieldsValue({
                category: selectedCategory?.id,
                subCategory: selectedSubCategory?.id,
              });
            }}
            dataSource={questions}
            header={selectedSubCategory ? <ItemName name={`Selected Sub-category: ${selectedSubCategory?.name}`} icon={<SubBoxIcon />} className="bold" /> : undefined}
            loading={questionStatus === "loading"}
            loadMore={
              totalQuestion < questions.length ? (
                <Button type="link" style={{ padding: 0, width: "100%" }} onClick={handleLoadMoreQuestions}>
                  Load More
                </Button>
              ) : null
            }
            onEdit={(item: any) => {
              setEditingRecord(item);
              setModalType("question");
              form.setFieldsValue({
                ...item,
                category: item?.subCategory?.category?.id || selectedCategory?.id,
                subCategory: item?.subCategory?.id || selectedSubCategory?.id,
              });
            }}
            onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "question" })}
          />
        </StyledContent>
      </WebView>

      <MobileView>
        <CollapseSection
          itemKey="1"
          label={
            <ItemLabel
              title="Category"
              buttonLabel="+ Add"
              hasAddPermission={hasPermission(permissions.AddEditCategory)}
              onButtonClick={() => {
                setModalType("category");
              }}
            />
          }
          children={
            <ItemList
              isStatus={true}
              hasAddPermission={hasPermission(permissions.AddEditCategory)}
              hasEditPermission={hasPermission(permissions.AddEditCategory)}
              loading={categoryStatus === "loading"}
              dataSource={sortOptionsAlphabetically(categories, "name")}
              isSelected={selectedCategory?.id}
              isSelectedItemOnClick={(item: any) => selectedCategory?.id !== item.id && handleCategoryClick(item)}
              onEdit={(item: any) => {
                setEditingRecord(item);
                form.setFieldsValue(item);
                setModalType("category");
              }}
              name={'Category'}
              onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "category" })}
              onItemClick={handleCategoryItemClick}
              isShowDelete={false}
            />
          }
        />

        <CollapseSection
          itemKey="2"
          activeKey={isSubCategoryOpen ? ["2"] : []}
          onClick={(e: any) => {
            e.stopPropagation(); // Prevents the click from affecting the collapse behavior
            setSubCategoryisopen(!isSubCategoryOpen);
          }}
          label={
            <ItemLabel
              title="Sub Category"
              buttonLabel="+ Add"
              hasAddPermission={hasPermission(permissions.AddEditSubCategory)}
              onButtonClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                form.setFieldsValue({ category: selectedCategory?.id });
                setModalType("subcategory");
              }}
              onTitleClick={() => setSubCategoryisopen(!isSubCategoryOpen)}
            />
          }
          children={
            <ItemList
              name={'Sub Category'}
              isStatus={true}
              hasAddPermission={hasPermission(permissions.AddEditSubCategory)}
              hasEditPermission={hasPermission(permissions.AddEditSubCategory)}
              hasDeletePermission={hasPermission(permissions.DeleteSubCategory)}
              header={selectedCategory ? <ItemName className="bold" icon={<BoxIcon />} name={`Selected Category: ${selectedCategory?.name}`} /> : undefined}
              loading={subCategoryStatus === "loading"}
              dataSource={selectedCategory?.id ? sortOptionsAlphabetically(subCategories?.filter((subCategory) => subCategory?.category?.id === selectedCategory?.id), "name") : []}
              isSelected={selectedSubCategory?.id}
              isSelectedItemOnClick={(item: any) => selectedSubCategory?.id !== item.id && handleSubcategoryClick(item)}
              onEdit={(item: any) => {
                setEditingRecord(item);
                form.setFieldsValue({
                  ...item,
                  category: item?.category?.id,
                });
                setModalType("subcategory");
              }}
              onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "subcategory" })}
              onItemClick={handleSubCategoryItemClick}
            />
          }
        />

        <CollapseSection
          itemKey="3"
          activeKey={isQuestionsOpen ? ["3"] : []}
          onClick={(e: any) => {
            e.stopPropagation(); // Prevents the click from affecting the collapse behavior
            setQuestionsisopen(!isQuestionsOpen);
          }}
          label={
            <ItemLabel
              title="Questions"
              buttonLabel="+ Add"
              hasAddPermission={hasPermission(permissions.AddEditQuestion)}
              onButtonClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setModalType("question");
                form.setFieldsValue({
                  category: selectedCategory?.id,
                  subCategory: selectedSubCategory?.id,
                });
              }}
              onTitleClick={() => setQuestionsisopen(!isQuestionsOpen)}
            />
          }
          children={
            <ItemList
              dataSource={questions}
              hasAddPermission={hasPermission(permissions.AddEditQuestion)}
              hasEditPermission={hasPermission(permissions.AddEditQuestion)}
              hasDeletePermission={hasPermission(permissions.DeleteQuestion)}
              hasViewPermission={hasPermission(permissions.ViewQuestion)}
              header={selectedSubCategory ? <ItemName name={`Selected Sub-category: ${selectedSubCategory?.name}`} icon={<SubBoxIcon />} className="bold" /> : undefined}
              loading={questionStatus === "loading"}
              loadMore={
                totalQuestion < questions.length ? (
                  <Button type="link" style={{ padding: 0, width: "100%" }} onClick={handleLoadMoreQuestions}>
                    Load More
                  </Button>
                ) : null
              }
              onEdit={(item: any) => {
                setEditingRecord(item);
                setModalType("question");
                form.setFieldsValue({
                  ...item,
                  category: item?.subCategory?.category?.id || selectedCategory?.id,
                  subCategory: item?.subCategory?.id || selectedSubCategory?.id,
                });
              }}
              onDelete={(item: any) => setShowDeleteConfirmItem({ item, type: "question" })}
              onItemClick={handleQuestionsItemClick}
            />
          }
        />
      </MobileView>

      <CreateEditCategoryModal onCancel={onCancel} form={form} handleSave={handleSave} isModalOpen={modalType === "category"} hasAddPermission={hasPermission(permissions.AddEditCategory)} hasEditPermission={hasPermission(permissions.AddEditCategory)} hasViewPermission={hasPermission(permissions.ViewCategory)} />
      <CreateEditSubCategoryModal onCancel={onCancel} form={form} handleSave={handleSave} isModalOpen={modalType === "subcategory"} hasAddPermission={hasPermission(permissions.AddEditSubCategory)} hasEditPermission={hasPermission(permissions.AddEditSubCategory)} hasViewPermission={hasPermission(permissions.ViewSubCategory)} />
      <CreateEditQuestionModal onCancel={onCancel} form={form} handleSave={handleSave} isModalOpen={modalType === "question"} hasAddPermission={hasPermission(permissions.AddEditQuestion)} hasEditPermission={hasPermission(permissions.AddEditQuestion)} hasViewPermission={checkHasPermission(userRole, permissions.ViewQuestion)} />

      {showDeleteConfirmItem?.item?.id && (
        <ConfirmationModal
          handleSave={() => handleDelete()}
          isModalOpen={!!showDeleteConfirmItem?.item?.id}
          onCancel={() => setShowDeleteConfirmItem(null)}
          title={`Delete ${showDeleteConfirmItem.type}`}
          message={`Are you sure you want to delete the ${showDeleteConfirmItem.type} ${["category", "subcategory"].includes(showDeleteConfirmItem.type) ? (showDeleteConfirmItem?.item as Category | SubCategory)?.name : (showDeleteConfirmItem?.item as Question)?.fieldName
            }?`}
          confirmButtonText="Delete"
          loading={isDeleting}
        />
      )}
    </StyledLayout>
  );
};

export default CategoryPage;
